import { useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

import { shouldRetryMe } from '@/hooks/use-auth-state';
import { api } from '@/lib/api';
import { isCompleteConnectionsPages, selectAllConnections } from '@/lib/connections-cache';
import { queryKeys } from '@/lib/query-keys';

// ONE cache for connections: ['me','connections'] holds a single shape,
// an infinite-query pages object, and both hooks below read it. An
// earlier split cached a flat Connection[] under this key (via
// fetchAllConnections) and an InfiniteData object under
// ['me','connections','paginated'] -- sibling keys whose prefixes
// overlap, with nothing keeping the two shapes straight but comments.

// The paginated read (the Matches list): TanStack keeps whatever pages
// have been scrolled into view and fetchNextPage loads more. No retry
// override -- list failures render the screen's own error/retry state,
// where TanStack's default backoff is not in the way.
export function useConnections() {
  return useInfiniteQuery({
    queryKey: queryKeys.connections(),
    queryFn: ({ pageParam }) => api.listMyConnections(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.nextCursor ? lastPage.nextCursor : undefined),
  });
}

// The complete-list read (badges, conversation header, reveal-request
// sections): same cache entry, driven to the end of the cursor chain --
// the on-screen equivalent of the old fetchAllConnections loop. Consumers
// see undefined until every page is loaded (isCompleteConnectionsPages),
// the same complete-list-or-nothing contract the flat queryFn had, then
// one flat Connection[] via selectAllConnections. When the Matches list
// has already paged to the end, this hook gets the tail pages for free
// from the shared cache.
export function useAllConnections(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const query = useInfiniteQuery({
    queryKey: queryKeys.connections(),
    queryFn: ({ pageParam }) => api.listMyConnections(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.nextCursor ? lastPage.nextCursor : undefined),
    enabled,
    retry: shouldRetryMe,
  });

  // Drive the shared cache to completion. Re-fires only when the
  // boundary flips: once the last page lands, hasNextPage is false and
  // this goes quiet.
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const complete = isCompleteConnectionsPages(query.data?.pages);

  return {
    ...query,
    data: complete ? selectAllConnections(query.data?.pages) : undefined,
    // isPending is overridden, not inherited: TanStack's own flag turns
    // false after the FIRST page, which would drop the badge/section
    // consumers' loading states while later pages are still loading.
    isPending: (query.isPending || !complete) && !query.isError,
  };
}
